'use client';

import { useLayoutEffect, useRef, useState } from 'react';

type OwnProps = {
  items: string[];
};

function Dot() {
  return (
    <span
      aria-hidden="true"
      className="mx-xs size-[2px] shrink-0 rounded-full bg-text-inverse"
    />
  );
}

// Dot-separated selections that truncate to "+N more" when they overflow the width.
export default function FooterSelections({ items }: OwnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const moreRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function measure() {
      const available = container?.clientWidth ?? 0;
      const moreWidth = moreRef.current?.offsetWidth ?? 0;

      let used = 0;
      let count = 0;

      for (let index = 0; index < items.length; index++) {
        const itemWidth = itemRefs.current[index]?.offsetWidth ?? 0;
        // Reserve room for the "+N more" chip when items remain after this one.
        const reserve = index < items.length - 1 ? moreWidth : 0;

        if (used + itemWidth + reserve > available) {
          break;
        }

        used += itemWidth;
        count++;
      }

      setVisibleCount(Math.max(1, count));
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  const hiddenCount = items.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className="relative flex w-full items-center overflow-hidden"
      data-testid="footer-selections"
    >
      {/* Hidden measurement row (absolute, clipped) so its width never affects layout. */}
      <div
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex items-center whitespace-nowrap"
      >
        {items.map((item, index) => (
          <span
            key={item}
            ref={element => {
              itemRefs.current[index] = element;
            }}
            className="t-label-sm text-text-inverse"
          >
            {item}
          </span>
        ))}
        <span ref={moreRef} className="t-label-sm text-text-inverse">
          <Dot />+{items.length} more
        </span>
      </div>

      {items.slice(0, visibleCount).map((item, index) => (
        <span key={item} className="flex shrink-0 items-center">
          {index > 0 && <Dot />}
          <span className="t-label-sm whitespace-nowrap text-text-inverse">
            {item}
          </span>
        </span>
      ))}

      {hiddenCount > 0 && (
        <span className="flex shrink-0 items-center">
          <Dot />
          <span className="t-label-sm whitespace-nowrap text-text-inverse">
            +{hiddenCount} more
          </span>
        </span>
      )}
    </div>
  );
}
